import { gql, useQuery } from '@apollo/client';
import { Tabs } from '@zillow/constellation-10';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { ReadRepliedStatus, RenterProgressStatus } from '../../types/GraphqlTypes';
import {
  APPLICANTS_FILTER_CLICK_EVENT_TYPE_ID,
  emitApplicantsFilterClickEvent,
  emitInquiriesFilterClickEvent,
  emitTenantsFilterClickEvent,
  emitToursFilterClickEvent,
  emitViewAllFilterClickEvent,
  INQUIRIES_FILTER_CLICK_EVENT_TYPE_ID,
  TENANTS_FILTER_CLICK_EVENT_TYPE_ID,
  TOURS_FILTER_CLICK_EVENT_TYPE_ID,
  VIEW_ALL_FILTER_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/filterTabs';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { useAuth } from '@zg-rentals/auth-comet';

const RentalJourneyFilters = (): JSX.Element => {
  const { data: reactiveVariableData } = useQuery(GET_IS_LOADING);
  const { renterProgressStatus, folder } = useContext(InboxPageContext);
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const [currentRenterProgressStatus, setCurrentRenterProgressStatus] = useState(renterProgressStatus);
  const { user } = useAuth();

  useEffect(() => {
    setCurrentRenterProgressStatus(renterProgressStatus);
  }, [renterProgressStatus]);

  const getEventDetailsForSelectedFilter = useCallback((selectedFilterTab: RenterProgressStatus) => {
    switch (selectedFilterTab) {
      case RenterProgressStatus.ShowAll:
        return emitViewAllFilterClickEvent;
      case RenterProgressStatus.Inquiries:
        return emitInquiriesFilterClickEvent;
      case RenterProgressStatus.Tours:
        return emitToursFilterClickEvent;
      case RenterProgressStatus.Applicants:
        return emitApplicantsFilterClickEvent;
      case RenterProgressStatus.Tenants:
        return emitTenantsFilterClickEvent;
      default:
        return;
    }
  }, []);

  const handleFilterClick = (selectedFilterTab: string) => {
    const filterTab = selectedFilterTab as RenterProgressStatus;
    if (reactiveVariableData?.inboxPageIsLoading || filterTab === currentRenterProgressStatus) {
      return;
    }

    const clickstreamEvent = getEventDetailsForSelectedFilter(filterTab);
    clickstreamEvent?.(user);

    setCurrentRenterProgressStatus(filterTab);
    changeFiltersOrFolders({
      renterProgressStatus: filterTab,
      folder,
      // Reset other filters
      readRepliedStatus: ReadRepliedStatus.ShowAll,
    });
  };

  return (
    <Tabs.Root
      appearance="default"
      defaultSelected={currentRenterProgressStatus}
      manualActivation
      onSelectedChange={handleFilterClick}
      css={{
        height: '100%',
        xxl: {
          marginLeft: 'default',
        },
      }}
    >
      <Tabs.List
        css={{
          height: '100%',
          xxl: {
            borderBottom: 'none',
          },
          minWidth: 'max-content',
          '& button[aria-selected=true]': { fontWeight: 'bold' },
        }}
      >
        <Tabs.Tab data-eventid={VIEW_ALL_FILTER_CLICK_EVENT_TYPE_ID} value={RenterProgressStatus.ShowAll}>
          View All
        </Tabs.Tab>
        <Tabs.Tab data-eventid={INQUIRIES_FILTER_CLICK_EVENT_TYPE_ID} value={RenterProgressStatus.Inquiries}>
          Inquiries
        </Tabs.Tab>
        <Tabs.Tab data-eventid={TOURS_FILTER_CLICK_EVENT_TYPE_ID} value={RenterProgressStatus.Tours}>
          Tours
        </Tabs.Tab>
        <Tabs.Tab data-eventid={APPLICANTS_FILTER_CLICK_EVENT_TYPE_ID} value={RenterProgressStatus.Applicants}>
          Applicants
        </Tabs.Tab>
        <Tabs.Tab data-eventid={TENANTS_FILTER_CLICK_EVENT_TYPE_ID} value={RenterProgressStatus.Tenants}>
          Tenants
        </Tabs.Tab>
      </Tabs.List>
    </Tabs.Root>
  );
};

export default memo(RentalJourneyFilters);

const GET_IS_LOADING = gql`
  query {
    inboxPageIsLoading @client
  }
`;
